import {useEffect, useState} from 'react';
import {ContentCopy, Edit, PowerSettingsNew, Timeline} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {Link} from 'react-router-dom';
import {PRIVATE_ADMIN_PLAYER_HEALTH} from 'config/router/paths';
import {
  downloadCsv,
  filterUsers,
  formatDate,
  isSoon,
  isExpired,
  linkName,
  userStatus
} from 'utils/adminManagement';
import {ToggleDialog, UserEditor} from './management/ManagementEditors';
import {
  EmptyResults,
  Expiry,
  FilterSelect,
  ManagementHeader,
  ManagementMessages,
  SearchField,
  SummaryCards
} from './management/ManagementUI';
import {useManagement, useManagementFilters} from './management/useManagement';

const DEFAULTS = {q: '', state: 'all', m3u: 'all', expiry: 'all', variant: 'all', sort: 'name'};

export default function UserManager() {
  const model = useManagement();
  const [filters, update, reset] = useManagementFilters(DEFAULTS);
  const [editor, setEditor] = useState(null);
  const [toggle, setToggle] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const filterKey = JSON.stringify(filters);
  const rows = filterUsers(model.users, model.m3us, filters);
  useEffect(() => {
    setPage(0);
  }, [filterKey]);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1));
  const copyPlaylist = async (user) => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
      await navigator.clipboard.writeText(`${base}/m3u/${encodeURIComponent(user.publicPath)}`);
      model.setFeedback({
        severity: 'success',
        message: `Enlace personal de ${user.name || user.email} copiado.`
      });
    } catch {
      model.setFeedback({severity: 'error', message: 'No se ha podido copiar el enlace personal.'});
    }
  };
  const exportRows = () =>
    downloadCsv('usuarios.csv', [
      [
        'ID',
        'Nombre',
        'Email',
        'Teléfono',
        'Estado',
        'M3U',
        'Pantallas máximas',
        'Caducidad',
        'Reproductor'
      ],
      ...rows.map((user) => [
        user.id,
        user.name,
        user.email,
        user.phone,
        userStatus(user, model.m3us).label,
        user.m3uId ? linkName(model.m3us.find((m) => m.id === user.m3uId)) : '',
        user.maxActiveStreams,
        user.m3uExpiresAt,
        user.playerVariant
      ])
    ]);
  return (
    <Box sx={{maxWidth: 1600, mx: 'auto'}}>
      <ManagementHeader
        section="users"
        title="Usuarios"
        description="Gestiona suscripciones, enlaces y pantallas. Cada cuenta conserva su enlace personal al cambiar de M3U."
        model={model}
        onAdd={() => setEditor({})}
        onExport={exportRows}
      />
      <SummaryCards
        items={[
          {
            label: 'Usuarios',
            value: model.users.length,
            hint: `${model.users.filter((u) => u.enabled).length} cuentas activadas`
          },
          {
            label: 'Sin M3U',
            value: model.users.filter((u) => !u.m3uId).length,
            hint: 'Pendientes de asociación',
            color: 'warning'
          },
          {
            label: 'Renovaciones próximas',
            value: model.users.filter((u) => isSoon(u.m3uExpiresAt)).length,
            hint: 'Suscripciones que caducan en 30 días',
            color: 'warning'
          },
          {
            label: 'Pantallas reservadas',
            value: model.users
              .filter((u) => u.m3uId)
              .reduce((total, u) => total + u.maxActiveStreams, 0),
            hint: 'Suma de los máximos con M3U asociado'
          }
        ]}
      />
      <Paper variant="outlined" sx={{p: 2, mb: 2}}>
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <SearchField
            label="Buscar nombre, email, teléfono o ruta"
            value={filters.q}
            onChange={(q) => update({q})}
          />
          <FilterSelect
            label="Estado"
            value={filters.state}
            onChange={(state) => update({state})}
            options={[
              ['all', 'Todos'],
              ['active', 'Activos'],
              ['disabled', 'Desactivados'],
              ['unassigned', 'Sin M3U'],
              ['expired', 'Suscripción caducada'],
              ['unavailable', 'Servicio no disponible']
            ]}
          />
          <FilterSelect
            label="M3U asociado"
            value={filters.m3u}
            onChange={(m3u) => update({m3u})}
            options={[
              ['all', 'Todos los enlaces'],
              ['none', 'Sin M3U'],
              ...model.m3us.map((m) => [String(m.id), linkName(m)])
            ]}
          />
          <FilterSelect
            label="Suscripción"
            value={filters.expiry}
            onChange={(expiry) => update({expiry})}
            options={[
              ['all', 'Todas'],
              ['soon', 'Caduca en 30 días'],
              ['expired', 'Caducada / sin fecha']
            ]}
          />
          <FilterSelect
            label="Reproductor"
            value={filters.variant}
            onChange={(variant) => update({variant})}
            options={[
              ['all', 'Todos'],
              ['A', 'A'],
              ['B', 'B'],
              ['C', 'C'],
              ['D', 'D']
            ]}
          />
          <FilterSelect
            label="Ordenar por"
            value={filters.sort}
            onChange={(sort) => update({sort})}
            options={[
              ['name', 'Nombre'],
              ['expiry', 'Caducidad más próxima'],
              ['screens', 'Más pantallas']
            ]}
          />
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mt: 1}}>
          <Typography variant="caption" color="text.secondary">
            {rows.length} de {model.users.length} usuarios · Edita una cuenta para asociarle un M3U
            o renovar su suscripción.
          </Typography>
          <Button size="small" onClick={reset}>
            Restablecer
          </Button>
        </Stack>
      </Paper>
      <ManagementMessages model={model} />
      <Paper variant="outlined">
        <TableContainer>
          <Table aria-label="Gestor de usuarios" sx={{minWidth: 1050}}>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>M3U asociado</TableCell>
                <TableCell align="center">Pantallas</TableCell>
                <TableCell>Suscripción</TableCell>
                <TableCell align="center">Reproductor</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map((user) => {
                const link = model.m3us.find((item) => item.id === user.m3uId);
                const status = userStatus(user, model.m3us);
                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Button
                        sx={{p: 0, textTransform: 'none', textAlign: 'left', fontWeight: 700}}
                        onClick={() => setEditor(user)}
                      >
                        {user.name || user.email}
                      </Button>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {user.email}
                      </Typography>
                      {user.phone && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {user.phone}
                        </Typography>
                      )}
                      {user.isAdmin && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Administrador"
                          sx={{mt: 0.5}}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={status.color}
                        label={status.label}
                      />
                    </TableCell>
                    <TableCell>
                      {link ? (
                        <>
                          <Typography variant="body2" fontWeight={600}>
                            {linkName(link)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Asignado: {formatDate(user.m3uAssignedAt)}
                          </Typography>
                        </>
                      ) : (
                        <Button size="small" onClick={() => setEditor(user)}>
                          Asociar M3U
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={700}>{user.maxActiveStreams}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        máximas
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Expiry value={user.m3uExpiresAt} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={user.playerVariant}
                        size="small"
                        color={user.playerVariant === 'D' ? 'secondary' : 'primary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                      <Tooltip title="Editar usuario">
                        <IconButton
                          onClick={() => setEditor(user)}
                          aria-label={`Editar ${user.email}`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copiar M3U personal">
                        <span>
                          <IconButton
                            disabled={!user.publicPath}
                            onClick={() => copyPlaylist(user)}
                            aria-label={`Copiar M3U de ${user.email}`}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Ver actividad">
                        <span>
                          <IconButton
                            disabled={!user.publicPath}
                            component={Link}
                            to={`${PRIVATE_ADMIN_PLAYER_HEALTH}?publicPath=${encodeURIComponent(
                              user.publicPath || ''
                            )}`}
                            aria-label={`Ver actividad de ${user.email}`}
                          >
                            <Timeline fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={user.enabled ? 'Desactivar' : 'Activar'}>
                        <IconButton
                          onClick={() => setToggle(user)}
                          aria-label={`${user.enabled ? 'Desactivar' : 'Activar'} ${user.email}`}
                        >
                          <PowerSettingsNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {!rows.length && (
          <EmptyResults loading={model.loading} onReset={reset}>
            No hay usuarios que coincidan con los filtros.
          </EmptyResults>
        )}
        <TablePagination
          component="div"
          count={rows.length}
          page={currentPage}
          rowsPerPage={pageSize}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Usuarios por página"
          labelDisplayedRows={({from, to, count}) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(type) => (type === 'next' ? 'Página siguiente' : 'Página anterior')}
        />
      </Paper>
      {model.generatedAt && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
          Actualizado: {formatDate(model.generatedAt)}. El CSV exporta los resultados filtrados.
        </Typography>
      )}
      {editor && (
        <UserEditor
          key={editor.id || 'new'}
          item={editor.id ? editor : null}
          links={model.m3us}
          initialM3uId={
            model.m3us.find(
              (link) =>
                link.id === Number(filters.m3u) && link.enabled && !isExpired(link.expiresAt)
            )?.id || null
          }
          onClose={() => setEditor(null)}
          onSaved={(result) => {
            setEditor(null);
            model.saved(result, 'Usuario guardado.');
          }}
        />
      )}
      {toggle && (
        <ToggleDialog
          kind="users"
          item={toggle}
          onClose={() => setToggle(null)}
          onSaved={(result) => {
            setToggle(null);
            model.saved(result, 'Estado del usuario actualizado.');
          }}
        />
      )}
    </Box>
  );
}
